/* Home Loan Eligibility Calculator Logic — Math, Charts & CSV Export */

let hleDonutChart = null;
let hleGrowthChart = null;

function getHleInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    income: getVal('hleIncome', 100000),
    emis: getVal('hleEmis', 0),
    rate: getVal('hleRate', 8.5),
    tenure: getVal('hleTenure', 20),
    foir: getVal('hleFoir', 50),
  };
}

function updateHleUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('income', params.income);
    url.searchParams.set('emis', params.emis);
    url.searchParams.set('rate', params.rate);
    url.searchParams.set('tenure', params.tenure);
    url.searchParams.set('foir', params.foir);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseHleUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const income = searchParams.get('income');
  const emis = searchParams.get('emis');
  const rate = searchParams.get('rate');
  const tenure = searchParams.get('tenure');
  const foir = searchParams.get('foir');

  return {
    income: income !== null ? +income : null,
    emis: emis !== null ? +emis : null,
    rate: rate !== null ? +rate : null,
    tenure: tenure !== null ? +tenure : null,
    foir: foir !== null ? +foir : null,
  };
}

function calculateLoanAmountFromEmi(maxEmi, rate, tenureYears) {
  if (maxEmi <= 0 || tenureYears <= 0) return 0;
  const n = tenureYears * 12;
  const r = rate / 12 / 100;

  if (r <= 0) return maxEmi * n;

  // P = EMI * [ (1+r)^n - 1 ] / [ r * (1+r)^n ]
  const num = Math.pow(1 + r, n) - 1;
  const den = r * Math.pow(1 + r, n);
  return maxEmi * (num / den);
}

function calculateHle(params) {
  const { income, emis, rate, tenure, foir } = params;

  const maxAllowedEmiTotal = income * (foir / 100);
  const maxEmiForHomeLoan = Math.max(0, maxAllowedEmiTotal - emis);

  const eligibleLoan = calculateLoanAmountFromEmi(maxEmiForHomeLoan, rate, tenure);

  const n = tenure * 12;
  const totalRepayment = maxEmiForHomeLoan * n;
  const totalInterest = Math.max(0, totalRepayment - eligibleLoan);
  const remainingDisposable = Math.max(0, income - maxAllowedEmiTotal);

  // Compare across 5, 10, 15, 20, 25, 30 year tenures
  const tenureComparison = [5, 10, 15, 20, 25, 30].map((t) => {
    const loan = calculateLoanAmountFromEmi(maxEmiForHomeLoan, rate, t);
    const months = t * 12;
    const repayment = maxEmiForHomeLoan * months;
    const interest = Math.max(0, repayment - loan);

    return {
      tenureYears: t,
      loanAmount: Math.round(loan),
      maxEmi: Math.round(maxEmiForHomeLoan),
      totalInterest: Math.round(interest),
      totalRepayment: Math.round(repayment),
    };
  });

  return {
    income: Math.round(income),
    existingEmis: Math.round(emis),
    maxEmi: Math.round(maxEmiForHomeLoan),
    eligibleLoan: Math.round(eligibleLoan),
    totalInterest: Math.round(totalInterest),
    totalRepayment: Math.round(totalRepayment),
    remainingDisposable: Math.round(remainingDisposable),
    tenureComparison,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('hleIncomeDisplay', fmt(params.income));
  setTxt('hleEmisDisplay', fmt(params.emis));
  setTxt('hleRateDisplay', `${params.rate}%`);
  setTxt('hleTenureDisplay', `${params.tenure} yrs`);
  setTxt('hleFoirDisplay', `${params.foir}%`);
}

function renderHleResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('hleVerdictHeadline', `Eligible Loan: ${fmt(result.eligibleLoan)}`);
  setTxt('hleVerdictDetail', `With a monthly income of ${fmt(params.income)} and ${params.foir}% FOIR limit, you qualify for a maximum home loan of ${fmt(result.eligibleLoan)} at ${params.rate}% for ${params.tenure} years.`);

  setTxt('hleMaxEmiValue', `${fmt(result.maxEmi)}/mo`);
  setTxt('hleTotalRepaymentValue', fmt(result.totalRepayment));
  setTxt('hleLoanMetricValue', fmt(result.eligibleLoan));
  setTxt('hleInterestMetricValue', fmt(result.totalInterest));
  setTxt('hleEmiMetricValue', `${fmt(result.maxEmi)}/mo`);

  renderTable(result.tenureComparison, params.tenure);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(result.maxEmi, params.emis, result.remainingDisposable);
      renderGrowthChart(result.tenureComparison);
    }
  } catch (err) {
    console.warn('Home Loan Eligibility Chart rendering error:', err);
  }
}

function renderDonutChart(homeLoanEmi, existingEmis, disposable) {
  const ctx = document.getElementById('hleDonutChart');
  if (!ctx) return;

  if (hleDonutChart) hleDonutChart.destroy();

  hleDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Home Loan EMI Capacity', 'Existing EMIs', 'Remaining Disposable Income'],
      datasets: [
        {
          data: [homeLoanEmi, existingEmis, disposable],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent'), '#3B82F6'],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(tenureComparison) {
  const ctx = document.getElementById('hleGrowthChart');
  if (!ctx) return;

  const labels = tenureComparison.map((r) => `${r.tenureYears} Yrs`);
  const loanData = tenureComparison.map((r) => r.loanAmount);
  const interestData = tenureComparison.map((r) => r.totalInterest);

  if (hleGrowthChart) hleGrowthChart.destroy();

  hleGrowthChart = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: 'Eligible Loan Principal',
          data: loanData,
          backgroundColor: cssVar('--chart-buy'),
          borderRadius: 6,
        },
        {
          label: 'Total Interest Payable',
          data: interestData,
          backgroundColor: cssVar('--chart-rent'),
          borderRadius: 6,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(comparison, activeTenure) {
  const tbody = document.getElementById('hleScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = comparison.map((row) => {
    const isSelected = row.tenureYears === activeTenure;
    return `
      <tr ${isSelected ? 'style="background: var(--bg-surface); font-weight: 600;"' : ''}>
        <td>${row.tenureYears} Years ${isSelected ? '⭐' : ''}</td>
        <td class="advantage-buyer">${fmt(row.loanAmount)}</td>
        <td>${fmt(row.maxEmi)}/mo</td>
        <td class="advantage-renter">${fmt(row.totalInterest)}</td>
        <td>${fmt(row.totalRepayment)}</td>
      </tr>
    `;
  }).join('');
}

function exportHleCsv() {
  const params = getHleInputs();
  const result = calculateHle(params);
  const headers = ['Tenure Years', 'Max Eligible Loan', 'Monthly EMI Capacity', 'Total Interest Payable', 'Total Repayment'];
  const rows = result.tenureComparison.map((r) => [
    r.tenureYears, r.loanAmount, r.maxEmi, r.totalInterest, r.totalRepayment
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `home-loan-eligibility-${params.tenure}yrs.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applyHleCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultIncome = 100000;
  let maxIncome = 10000000;
  let stepIncome = 5000;

  if (curr === 'INR') {
    defaultIncome = 100000;
    maxIncome = 5000000;
    stepIncome = 5000;
  } else if (curr === 'JPY') {
    defaultIncome = 1000000;
    maxIncome = 50000000;
    stepIncome = 50000;
  }

  const iSlider = document.getElementById('hleIncome');
  if (iSlider) {
    iSlider.max = maxIncome;
    iSlider.step = stepIncome;
    iSlider.value = overrideParams.income ?? defaultIncome;
  }

  const eSlider = document.getElementById('hleEmis');
  if (eSlider && overrideParams.emis !== null) {
    eSlider.value = overrideParams.emis;
  }

  const rSlider = document.getElementById('hleRate');
  if (rSlider && overrideParams.rate !== null) {
    rSlider.value = overrideParams.rate;
  }

  const tSlider = document.getElementById('hleTenure');
  if (tSlider && overrideParams.tenure !== null) {
    tSlider.value = overrideParams.tenure;
  }

  const fSlider = document.getElementById('hleFoir');
  if (fSlider && overrideParams.foir !== null) {
    fSlider.value = overrideParams.foir;
  }
}

function recalculateHle() {
  const params = getHleInputs();
  updateDisplays(params);
  updateHleUrlParams(params);
  const result = calculateHle(params);
  renderHleResults(result, params);
}

function initHleCalculator() {
  if (!document.getElementById('hleIncome')) return;

  const urlParams = parseHleUrlParams();

  initCountry((country) => {
    applyHleCountryRanges(country, urlParams);
    recalculateHle();
  });

  const inputs = ['hleIncome', 'hleEmis', 'hleRate', 'hleTenure', 'hleFoir'];
  inputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', recalculateHle);
      el.addEventListener('change', recalculateHle);
    }
  });

  const exportBtn = document.getElementById('exportHleCsv');
  if (exportBtn) {
    exportBtn.addEventListener('click', exportHleCsv);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initHleCalculator();
});
